import { Feature } from "ol";
import { Geometry } from "ol/geom";
import RenderFeature from "ol/render/Feature";
import { Map as MGMap } from '@meteogroup/map-layers';
import { FleetIcons } from "./map-styles";
import { VesselIconZoomMinimumLevels, VesselIconType } from "./types/map-draw-utils.types";
import { PositionAgeStatus } from "./types/voyage-layers.types";
import { Icon, Style } from "ol/style";
import { vesselColorsMap } from "../utils/fleet-point-icon-utils";
import { VesselCategories } from "../services/types/position-service.types";

export function getVesselIconOpacity(positionAge: PositionAgeStatus, isMaersk: boolean): number {
    if (positionAge === PositionAgeStatus.NORMAL) {
        return 1;
    } else if (positionAge === PositionAgeStatus.OUTDATED) {
        return 0.4;
    } else if (!isMaersk && positionAge === PositionAgeStatus.OBSOLETE) {
        return 0;
    }
    return 1;
}

export function setVesselStyling(ctx: CanvasRenderingContext2D, options: {drawInReallifeSize: boolean, positionAge: PositionAgeStatus, isMaersk: boolean, fillColor: string}) {
    const { drawInReallifeSize, fillColor, positionAge, isMaersk} = options;
    ctx.globalAlpha = getVesselIconOpacity(positionAge, isMaersk);
    ctx.strokeStyle = '#000000';
    ctx.lineWidth = drawInReallifeSize ? 3 : 4;
    ctx.fillStyle = fillColor;

}

// detach from previous cavnas transforms and stylings and set location
export function resetAndCenter(ctx: CanvasRenderingContext2D, coords: number[]) {
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.translate(coords[0], coords[1]);
}

export function drawHighlightCircle(ctx: CanvasRenderingContext2D, options: {length: number, width: number, circleOffset: number, circleRadius: number, coords: number[], theading: number, drawInReallifeSize: boolean, highlightCircleColor: string}) {
    const { length, width, circleOffset, circleRadius, coords, theading, drawInReallifeSize, highlightCircleColor } = options;
    const dashSize = 3;
    resetAndCenter(ctx, coords);
    ctx.strokeStyle = '#000000';
    ctx.lineWidth = drawInReallifeSize ? 3 : 4;
    ctx.rotate(theading);
    ctx.lineWidth = dashSize;
    ctx.setLineDash([dashSize, dashSize]);
    ctx.moveTo(-length, -width);
    ctx.beginPath();
    ctx.arc(circleOffset, 0, circleRadius, 0, Math.PI*2);
    ctx.closePath();
    ctx.strokeStyle = highlightCircleColor;
    ctx.stroke();
}

export function drawFocusTriangle(ctx: CanvasRenderingContext2D, options: {length: number, width: number, coords: number[], theading: number,  highlightColor: string, fillColor: string}) {
    const { length, width, coords, theading, highlightColor, fillColor } = options;
    const dashSize =1;
    resetAndCenter(ctx, coords);
    ctx.fillStyle = fillColor; // Set fill color
    ctx.lineWidth = 10;
    ctx.rotate(theading);
    ctx.lineWidth = dashSize;
    // Draw the filled triangle
    ctx.beginPath();
    ctx.moveTo(-length, -width);
    ctx.lineTo(length, 0);
    ctx.lineTo(-length, width);
    ctx.closePath();

    ctx.fill(); // Fill the triangle with the specified color
    ctx.strokeStyle = highlightColor;
    ctx.stroke();
}

export function drawRealLifeSizeVessel(ctx: CanvasRenderingContext2D, options: {theading: number, drawLength: number, drawWidth: number, bowOffset: number, resolution: number, isSelected: boolean}) {
    const { theading, drawLength, drawWidth, bowOffset, resolution, isSelected } = options;
    ctx.rotate(theading);
    ctx.beginPath();
    ctx.moveTo(-drawLength, -drawWidth);
    ctx.lineTo(drawLength - bowOffset, -drawWidth);
    ctx.quadraticCurveTo(drawLength + bowOffset, 0, drawLength - bowOffset, drawWidth);
    ctx.lineTo(-drawLength, drawWidth);
    ctx.closePath();
    ctx.fill();
    ctx.strokeStyle = isSelected ? "#90E3FF" : "#000000";
    ctx.lineWidth = ctx.lineWidth/resolution;
    ctx.stroke();
}

export function drawVesselWithoutHeading(ctx: CanvasRenderingContext2D, width: number, isSelected: boolean) {
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.rotate(Math.PI/4.0);
    ctx.rect(-width, -width, width*2, width*2);
    ctx.fill();
    ctx.strokeStyle = isSelected ? "#90E3FF" : "#000000";
    ctx.stroke();
}

// these values affect vessel icons and vessel highlight circle
export function getVesselDrawingCharacteristics(feature: RenderFeature | Feature<Geometry>, options: {maxVesselLength: number, resolution: number, map: MGMap|null, desiredScale: number, zoom: number|null}) {
    const { maxVesselLength, resolution, map, desiredScale, zoom } = options;
    const heading = feature.get('heading');
    let length = 18;
    let width = 14;
    let drawInReallifeSize = false;
    let scaleFactor = desiredScale;
    const highlight = feature.get('highlight');
    const vesselIconType = getVesselIconType(map!, heading);
    const isMaersk = feature.get("isMaersk");
    const isSelected = feature.get("isSelected");
    
    if (zoom! > VesselIconZoomMinimumLevels.REAL_LIFE) {
        drawInReallifeSize = true;
        length = feature.get('length');
        width = feature.get('width');
    }

    if (vesselIconType !== VesselIconType.RealLifeSize) {
        scaleFactor *= (0.8 * length! / maxVesselLength + 0.2);
    }
    else if (vesselIconType !== VesselIconType.RealLifeSize && highlight) {
        scaleFactor *= 1.15
    }
    return {
        length,
        width,
        heading,
        theading: ((heading || 90) - 90) * Math.PI / 180, // in case of no heading do not rotate
        highlight:  feature.get('highlight'),
        scaleFactor,
        drawInReallifeSize,
        hasAlarms: feature.get('vesselInfo')?.alarms.length > 0,
        positionAge: feature.get('positionAge'),
        bowOffset: length/3*2*2,
        tailOffSet: length/3*2,
        resolutionScale: 1/resolution,
        vesselIconType,
        isMaersk,
        isSelected,
    }
}

export function getVesselIconType(map: MGMap, heading: number): VesselIconType {
    const zoomLevel = map.getView().getZoom()!;
    
    if (zoomLevel < VesselIconZoomMinimumLevels.NORMAL) {
        return VesselIconType.Dot;
    }
    if (!heading) {
        return VesselIconType.MissingHeading;
    }
    if (zoomLevel >= VesselIconZoomMinimumLevels.REAL_LIFE) {
        return VesselIconType.RealLifeSize;
    }
    return VesselIconType.Normal;
}

export function getNormalVesselIcon(vesselCategory: VesselCategories, isSelected: string, heading: number) {
    let svg = ""
    if (heading) {
        svg = FleetIcons.vesselShape;
    } else {
        svg = FleetIcons.missingHeading;
    }

    const color = vesselColorsMap.get(vesselCategory)!;
    svg = svg.replace("fillColor", color);
    svg = svg.replace("strokeColor", isSelected ? "#90E3FF" : "#141414");

    return new Style({
        image: new Icon({
            src: 'data:image/svg+xml;utf8,' + escape(svg),
            scale: 1,
            anchor: [0.5, 0.5],
            rotateWithView: heading ? false : true,
        })
    })
}
